import type { ChauffeurPage } from "../slug-chauffeur";

import heroImage from "@/assets/images/chauffeur/cote-azur/chauffeur-cote-azur.png";
import { cityPriceFaq } from "./cityPricing";

const vallaurisGolfeJuan: ChauffeurPage = {
  slug: "vallauris-golfe-juan",
  title: "Chauffeur privé à Vallauris Golfe-Juan – VTC 24h/24",
  subtitle:
    "Chauffeur privé et VTC à Vallauris Golfe-Juan : transferts aéroport de Nice, port du Golfe-Juan, céramique Picasso et plages, 7j/7.",
  keywords: [
    "chauffeur privé vallauris",
    "vtc golfe-juan",
    "chauffeur vallauris golfe-juan",
    "transfert golfe-juan aéroport nice",
  ],
  hero: {
    title: "Chauffeur privé & VTC à Vallauris Golfe-Juan",
    subtitle:
      "Transferts aéroport de Nice, port du Golfe-Juan, cité de la céramique et plages — disponible 24h/24, 7j/7.",
    keywords: [
      "VTC Golfe-Juan",
      "chauffeur Vallauris",
      "transfert aéroport Nice",
      "port Camille Rayon",
    ],
    imageSrc: heroImage.src,
    imageAlt: "Chauffeur privé & VTC à Vallauris Golfe-Juan",
    badges: ["Disponible 24h/24", "Suivi de vol", "Chauffeurs bilingues"],
  },
  benefits: {
    heading: "Pourquoi un chauffeur à Vallauris Golfe-Juan",
    items: [
      {
        title: "Aéroport de Nice en direct",
        description:
          "Aéroport de Nice à environ 30 min par l’A8, suivi de vol et accueil personnalisé.",
      },
      {
        title: "Port & plages",
        description:
          "Port Camille Rayon, plages du Midi et du Soleil : dépose au plus près, pour équipages, plaisanciers et invités.",
      },
      {
        title: "Chauffeurs bilingues",
        description:
          "FR/EN, ponctuels et discrets, à l’aise entre la cité potière et le front de mer.",
      },
      {
        title: "Ancrage local",
        description: "Basés sur la Côte d’Azur, note 5,0/5 sur 38 avis Google.",
      },
    ],
  },
  services: {
    heading: "Nos services de chauffeur à Vallauris Golfe-Juan",
    services: [
      {
        title: "Transfert aéroport de Nice",
        text: "Prise en charge avec suivi de vol depuis et vers l’aéroport de Nice Côte d’Azur et les gares.",
      },
      {
        title: "Port & plaisance",
        text: "Liaisons avec le port Camille Rayon et le front de mer de Golfe-Juan pour équipages et invités.",
      },
      {
        title: "Mise à disposition",
        text: "À l’heure ou à la journée, pour la cité de la céramique, les ateliers et les musées.",
      },
      {
        title: "Culture Picasso & céramique",
        text: "Musée national Picasso, musée Magnelli et ateliers de potiers, avec dépose au cœur de Vallauris.",
      },
      {
        title: "Longue distance",
        text: "Trajets toute distance depuis Golfe-Juan : Cannes, Antibes, Monaco, Saint-Tropez et Italie.",
      },
    ],
  },
  locations: {
    heading: "Lieux & points de prise en charge",
    items: [
      {
        name: "Port Camille Rayon",
        description: "Port de plaisance de Golfe-Juan, quais et front de mer.",
      },
      {
        name: "Musée national Picasso",
        description:
          "La Guerre et la Paix, dans le château-musée de Vallauris.",
      },
      {
        name: "Cité de la céramique",
        description: "Ateliers de potiers et boutiques au cœur de Vallauris.",
      },
      {
        name: "Plages du Midi & du Soleil",
        description: "Plages de sable le long de Golfe-Juan.",
      },
      {
        name: "Débarquement de Napoléon",
        description:
          "Golfe-Juan, point de départ historique de la Route Napoléon.",
      },
      {
        name: "Aéroport de Nice",
        description: "À environ 30 minutes par l’autoroute A8.",
      },
    ],
  },
  generic: {
    heading: "Un chauffeur privé pour tout Vallauris Golfe-Juan",
    subheading:
      "De la cité potière au port de plaisance, un service premium entre culture et mer.",
    paragraphs: [
      "KS Rent Services assure vos déplacements avec chauffeur privé à Vallauris Golfe-Juan et sur toute la Côte d’Azur : transferts depuis l’aéroport de Nice (environ 30 minutes), liaisons avec le port et trajets longue distance.",
      "Deux visages en une ville : Vallauris, capitale de la céramique marquée par Picasso, sur les hauteurs, et Golfe-Juan, station balnéaire et port de plaisance au bord de l’eau. Nos chauffeurs bilingues relient les deux sans effort.",
      "Entre une visite du musée national Picasso, le marché des potiers et un déjeuner au port Camille Rayon, votre chauffeur enchaîne les étapes et vous dépose au plus près, sans souci de stationnement.",
    ],
    bullets: [
      "Suivi de vol & accueil personnalisé",
      "Liaison port ⇄ cité potière",
      "Dépose au cœur de Vallauris",
    ],
  },
  process: {
    heading: "Réserver son chauffeur à Vallauris Golfe-Juan",
    steps: [
      "Indiquez votre trajet, vos horaires et le nombre de passagers.",
      "Nous confirmons le véhicule adapté et le forfait.",
      "Votre chauffeur suit votre vol et vous accueille à l’heure.",
      "Ajustements en direct selon le trafic et vos imprévus.",
    ],
  },
  faq: [
    cityPriceFaq("fr", "Vallauris Golfe-Juan"),
    {
      q: "Combien de temps pour rejoindre l’aéroport de Nice depuis Golfe-Juan ?",
      a: "Comptez environ 30 minutes par l’autoroute A8 dans des conditions normales. Nous assurons les transferts 24h/24 avec suivi de vol et accueil personnalisé.",
    },
    {
      q: "Assurez-vous les liaisons avec le port du Golfe-Juan ?",
      a: "Oui, nous rejoignons le port Camille Rayon pour les équipages, plaisanciers et invités, avec accueil au quai.",
    },
    {
      q: "Vos chauffeurs parlent-ils anglais ?",
      a: "Oui, nos chauffeurs sont bilingues français/anglais, habitués à une clientèle internationale.",
    },
    {
      q: "Quels véhicules proposez-vous à Vallauris Golfe-Juan ?",
      a: "Berline Mercedes Classe E et Classe S, van 7 places, SUV et minibus VIP, selon le nombre de passagers et l’occasion.",
    },
    {
      q: "Combien de temps à l’avance faut-il réserver ?",
      a: "Réservez au plus tôt en haute saison et pendant les temps forts de la céramique ; nous traitons aussi les demandes de dernière minute selon disponibilité.",
    },
    {
      q: "Emmenez-vous les visiteurs sur les traces de Picasso et de la céramique ?",
      a: "Oui, musée national Picasso, musée Magnelli et ateliers de potiers, avec dépose au cœur de la cité et attente sur place.",
    },
  ],
  relatedLinks: [
    { label: "Chauffeur privé à Cannes", href: "/chauffeur/cannes" },
    { label: "Réserver un chauffeur à Nice", href: "/chauffeur/nice" },
    { label: "Réserver un transfert aéroport", href: "/chauffeur/aeroport" },
    {
      label: "Mise à disposition avec chauffeur Côte d’Azur",
      href: "/chauffeur/cote-d-azur",
    },
  ],
  cta: {
    title: "Votre chauffeur privé à Vallauris Golfe-Juan",
    text: "Réservez votre transfert ou votre mise à disposition — réponse rapide, 7j/7.",
    primaryHref: "/reservation",
    primaryLabel: "Réserver / demander un devis",
  },
  seo: {
    title: "Chauffeur privé Vallauris Golfe-Juan — VTC 24h/24",
    description:
      "Chauffeur privé et VTC à Vallauris Golfe-Juan : transfert aéroport de Nice (~30 min), port Camille Rayon, céramique et musée Picasso. 5,0/5 sur 38 avis.",
    canonical: "/chauffeur/vallauris-golfe-juan",
  },
};

export default vallaurisGolfeJuan;
